import { useEffect, useMemo, useState } from 'react';
import { apiSetFunnelSteps, type FunnelDetail, type FunnelOffer } from '~/lib/api/funnels';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  Section,
  Select,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import {
  apiErrorMessage,
  BRANCHING_STEP_TYPES,
  branchTargets,
  draftsEqual,
  draftsFromSteps,
  draftsToInputs,
  formatMinor,
  moveStep,
  newStepDraft,
  OFFER_STEP_TYPES,
  removeStep,
  starterDrafts,
  STEP_TYPES,
  stepProblems,
  stepTypeLabel,
  type StepDraft,
} from './funnels-model';

const RULES =
  'One checkout. Order bumps sit right after it and show as checkboxes on the checkout page. ' +
  'Upsells and downsells come after the checkout and can jump forward. The last step is a thank-you.';

export function StepsEditor({
  appId,
  detail,
  onSaved,
}: {
  appId: string;
  detail: FunnelDetail;
  onSaved: (next: FunnelDetail) => void;
}) {
  const saved = useMemo(() => draftsFromSteps(detail.steps), [detail.steps]);
  const savedKey = JSON.stringify(detail.steps);
  const [drafts, setDrafts] = useState<StepDraft[]>(() => (saved.length ? saved : starterDrafts()));
  const [addType, setAddType] = useState('upsell');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // A save (or another writer, e.g. the agent) changed the stored steps: start from them.
  useEffect(() => {
    if (saved.length) {
      setDrafts(saved);
    }
  }, [savedKey]);

  const offerIds = useMemo(() => new Set(detail.offers.map((o) => o.id)), [detail.offers]);
  const problems = stepProblems(drafts, offerIds);
  const dirty = !draftsEqual(drafts, saved);

  const edit = (next: StepDraft[]) => {
    setDrafts(next);
    setJustSaved(false);
    setError(null);
  };

  const update = (i: number, patch: Partial<StepDraft>) =>
    edit(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const add = () => {
    const step = newStepDraft(addType, drafts);
    const last = drafts.length - 1;

    // New steps go before the closing thank-you, unless it's another thank-you.
    if (addType !== 'thank-you' && last >= 0 && drafts[last].type === 'thank-you') {
      edit([...drafts.slice(0, last), step, drafts[last]]);
    } else {
      edit([...drafts, step]);
    }
  };

  const save = async () => {
    if (problems.length) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      onSaved(await apiSetFunnelSteps(appId, detail.funnel.id, draftsToInputs(drafts)));
      setJustSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the steps.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Steps" icon="i-ph:path">
      <p className="text-[11px] leading-relaxed text-muted">{RULES}</p>

      {!detail.offers.length && (
        <p className="text-[11px] leading-relaxed text-warning">
          Checkout, bump and upsell steps need an offer. Add one in Offers first.
        </p>
      )}

      <ol className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
        {drafts.map((step, i) => (
          <StepRow
            key={step.key}
            index={i}
            step={step}
            drafts={drafts}
            offers={detail.offers}
            currency={detail.funnel.currency}
            disabled={saving}
            onChange={(patch) => update(i, patch)}
            onMove={(by) => edit(moveStep(drafts, i, by))}
            onRemove={() => edit(removeStep(drafts, i))}
          />
        ))}
      </ol>

      <div className="flex items-center gap-2">
        <Select value={addType} onChange={setAddType} label="Step type to add" className="w-36">
          {STEP_TYPES.map((t) => (
            <option key={t} value={t}>
              {stepTypeLabel(t)}
            </option>
          ))}
        </Select>
        <button type="button" onClick={add} disabled={saving} className={BTN_GHOST}>
          <div className="i-ph:plus text-[13px]" />
          Add step
        </button>
      </div>

      {problems.length > 0 && (
        <div className="ui-banner-warning items-start leading-relaxed">
          <div className="i-ph:warning mt-px shrink-0 text-sm text-warning" />
          <ul className="space-y-0.5 text-[12px] text-warning/90">
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || !dirty || problems.length > 0}
          className={BTN_SECONDARY}
        >
          {saving && <Spinner />}
          {saving ? 'Saving…' : 'Save steps'}
        </button>
        {dirty && saved.length > 0 && (
          <button type="button" onClick={() => edit(saved)} disabled={saving} className={BTN_GHOST}>
            Discard changes
          </button>
        )}
        {justSaved && !dirty && <span className="text-[11px] text-muted">Saved</span>}
      </div>
    </Section>
  );
}

function StepRow({
  index,
  step,
  drafts,
  offers,
  currency,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  step: StepDraft;
  drafts: StepDraft[];
  offers: FunnelOffer[];
  currency: string;
  disabled: boolean;
  onChange: (patch: Partial<StepDraft>) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
}) {
  const needsOffer = OFFER_STEP_TYPES.has(step.type);
  const branches = BRANCHING_STEP_TYPES.has(step.type);
  const targets = branches ? branchTargets(drafts, index) : [];
  const targetLabel = (d: StepDraft) => `${drafts.indexOf(d) + 1}. ${stepTypeLabel(d.type)} ${d.path}`;

  return (
    <li className="space-y-2 px-3 py-3">
      <div className="flex items-center gap-2">
        <span className="w-5 shrink-0 text-center font-geist-mono text-[11px] text-muted">{index + 1}</span>
        <Select
          value={step.type}
          onChange={(type) => onChange({ type })}
          label={`Step ${index + 1} type`}
          disabled={disabled}
          className="w-32 shrink-0"
        >
          {STEP_TYPES.map((t) => (
            <option key={t} value={t}>
              {stepTypeLabel(t)}
            </option>
          ))}
        </Select>
        <input
          value={step.path}
          onChange={(e) => onChange({ path: e.target.value })}
          aria-label={`Step ${index + 1} path`}
          spellCheck={false}
          disabled={disabled}
          className="ui-input-mono min-w-0 flex-1 !py-2 !text-[12px]"
        />
        <div className="flex shrink-0 items-center">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={disabled || index === 0}
            className="ui-btn-icon disabled:opacity-30"
            aria-label="Move step up"
            title="Move up"
          >
            <div className="i-ph:arrow-up text-[12px]" />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={disabled || index === drafts.length - 1}
            className="ui-btn-icon disabled:opacity-30"
            aria-label="Move step down"
            title="Move down"
          >
            <div className="i-ph:arrow-down text-[12px]" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="ui-btn-icon"
            aria-label="Remove step"
            title="Remove step"
          >
            <div className="i-ph:trash text-[12px]" />
          </button>
        </div>
      </div>

      {(needsOffer || branches) && (
        <div className="space-y-2 pl-7">
          {needsOffer && (
            <Select
              value={step.offerId ?? ''}
              onChange={(v) => onChange({ offerId: v || null })}
              label={`Step ${index + 1} offer`}
              disabled={disabled}
            >
              <option value="">Choose an offer…</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.title} · {formatMinor(o.amount, currency)}
                </option>
              ))}
            </Select>
          )}

          {branches && (
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['onAccept', 'If accepted'],
                  ['onDecline', 'If declined'],
                ] as const
              ).map(([field, label]) => (
                <label key={field} className="block space-y-1">
                  <span className="block text-[10px] text-muted">{label}</span>
                  <Select
                    value={step[field] ?? ''}
                    onChange={(v) => onChange({ [field]: v || null })}
                    label={`Step ${index + 1} ${label.toLowerCase()}`}
                    disabled={disabled}
                  >
                    <option value="">Next step</option>
                    {targets.map((t) => (
                      <option key={t.key} value={t.key}>
                        {targetLabel(t)}
                      </option>
                    ))}
                    {/* Keep a now-invalid pointer visible so the problem list can name it. */}
                    {step[field] && !targets.some((t) => t.key === step[field]) && (
                      <option value={step[field]!}>Invalid target</option>
                    )}
                  </Select>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}
